// ExpenseSelectionContext.js - New context for Expenses
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";

const ExpenseSelectionContext = createContext();

export const ExpenseSelectionProvider = ({ children }) => {
  // Filters state
  const [filters, setFilters] = useState({
    type: "",
    startDate: "",
    endDate: "",
    minAmount: "",
    maxAmount: "",
    resourceName: "",
    typeOfResource: "",
    courtRef: "",
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 5,
    hasMore: true,
    totalExpenses: 0,
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Store filtered expenses
  const [filteredExpenses, setFilteredExpenses] = useState([]);

  const lastUpdateTimeRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerExpenseRefresh = useCallback(() => {
    setRefreshKey((prevKey) => prevKey + 1);
  }, []);

  const setFilteredExpensesCallback = useCallback((expenses) => {
    const now = Date.now();

    if (now - lastUpdateTimeRef.current < 50) {
      return;
    }
    lastUpdateTimeRef.current = now;

    setFilteredExpenses(Array.isArray(expenses) ? [...expenses] : []);

    // Set loading to false after expenses are updated
    setIsLoading(false);
  }, []);

const buildQueryParams = useCallback(() => {
  const params = new URLSearchParams();

  // Pagination - skip if "all" is selected
  if (pagination.limit !== "all") {
    params.set("page", pagination.page.toString());
    params.set("limit", pagination.limit.toString());
  }

  // Search term
  if (searchTerm && searchTerm.trim()) {
    params.set("search", searchTerm.trim());
  }

  // FIXED: Apply all filters with proper empty string checking and exclude "all" values
  if (filters.type && filters.type.trim() !== "" && filters.type.trim() !== "all") {
    params.set("expenseType", filters.type.trim());
  }

  if (filters.startDate && filters.startDate.trim() !== "" && filters.startDate.trim() !== "all") {
    params.set("startDate", filters.startDate.trim());
  }

  if (filters.endDate && filters.endDate.trim() !== "" && filters.endDate.trim() !== "all") {
    params.set("endDate", filters.endDate.trim());
  }

  if (filters.minAmount && filters.minAmount.trim() !== "" && filters.minAmount.trim() !== "all") {
    params.set("minAmount", filters.minAmount.trim());
  }

  if (filters.maxAmount && filters.maxAmount.trim() !== "" && filters.maxAmount.trim() !== "all") {
    params.set("maxAmount", filters.maxAmount.trim());
  }

  if (filters.resourceName && filters.resourceName.trim() !== "" && filters.resourceName.trim() !== "all") {
    params.set("resourceName", filters.resourceName.trim());
  }

  if (filters.typeOfResource && filters.typeOfResource.trim() !== "" && filters.typeOfResource.trim() !== "all") {
    params.set("typeOfResource", filters.typeOfResource.trim());
  }

  if (filters.courtRef && filters.courtRef.trim() !== "" && filters.courtRef.trim() !== "all") {
    params.set("courtRef", filters.courtRef.trim());
  }

  return params.toString();
}, [pagination, searchTerm, filters]);


  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      page: 0,
      hasMore: true,
    }));
  }, []);

  // Clear all filters and reset state
  const clearAllFilters = useCallback(() => {
    setIsLoading(true);
    setFilters({
      type: "",
      startDate: "",
      endDate: "",
      minAmount: "",
      maxAmount: "",
      resourceName: "",
      typeOfResource: "",
      courtRef: "",
    });

    setSearchTerm("");
    resetPagination();
    // Don't clear filtered expenses here - let the API call handle it
  }, [resetPagination]);

  const refreshExpensesWithFilters = useCallback(async () => {
    // This would typically be called by the component after setting filters
    setIsLoading(true);
  }, []);

  // Stable setters
  const setFiltersStable = useCallback(
    (newFilters) => {
      setFilters((prevFilters) => {
        const hasChanged = Object.keys(newFilters).some(
          (key) => newFilters[key] !== prevFilters[key]
        );

        if (hasChanged) {
          resetPagination();
          return newFilters;
        }

        return prevFilters;
      });
    },
    [resetPagination]
  );

  const setSearchTermStable = useCallback(
    (term) => {
      if (term !== searchTerm) {
        setSearchTerm(term);
        resetPagination();
      }
    },
    [searchTerm, resetPagination]
  );

const hasActiveFilters = useMemo(() => {
  const hasSearchTerm = searchTerm && searchTerm.trim() !== "";
  const hasFilterValues = Object.values(filters).some((value) => 
    value && typeof value === 'string' && value.trim() !== ""
  );
  
  return hasSearchTerm || hasFilterValues;
}, [searchTerm, filters]);

  // Update pagination info
  const updatePaginationInfo = useCallback((totalExpenses, hasMore) => {
    setPagination((prev) => ({
      ...prev,
      totalExpenses,
      hasMore,
    }));
  }, []);

  // Load more expenses (for pagination)
  const loadMore = useCallback(() => {
    if (pagination.hasMore) {
      setPagination((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  }, [pagination.hasMore]);

const contextValue = useMemo(
  () => ({
    // Filters
    filters,
    setFilters: setFiltersStable,

    // Search
    searchTerm,
    setSearchTerm: setSearchTermStable,

    // Pagination
    pagination,
    setPagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,

    // Filtered expenses
    filteredExpenses,
    setFilteredExpenses: setFilteredExpensesCallback,
    refreshKey,
    triggerExpenseRefresh,

    // Utilities
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,

    // Loading state
    setIsLoading,
    isLoading,
  }),
  [
    filters,
    setFiltersStable,
    searchTerm,
    setSearchTermStable,
    pagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    filteredExpenses,
    setFilteredExpensesCallback,
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    refreshKey,
    triggerExpenseRefresh,
    setIsLoading,
    isLoading,
  ]
);

  return (
    <ExpenseSelectionContext.Provider value={contextValue}>
      {children}
    </ExpenseSelectionContext.Provider>
  );
};

export const useExpenseSelection = () => {
  const context = useContext(ExpenseSelectionContext);
  if (!context) {
    throw new Error(
      "useExpenseSelection must be used within an ExpenseSelectionProvider"
    );
  }
  return context;
};
